import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Shield, Car, Building2, Heart, Home as HomeIcon, Plane, Award, Users, Star, ArrowRight, CheckCircle2, Smartphone, Bike, Truck, Tractor, Zap, Gamepad2, Laptop, CreditCard, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteForm from "@/components/QuoteForm";
import InsuranceCalculator from "@/components/InsuranceCalculator";
import { trackWhatsApp } from "@/components/layout/WhatsAppButton";

function AnimatedCounter({ target, suffix = "", duration = 1800 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const num = parseFloat(target);
    const isDecimal = target.includes(".");
    const steps = 40;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) { setCount(num); clearInterval(timer); }
      else setCount(isDecimal ? Math.round(current * 10) / 10 : Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return <span ref={ref}>{target.includes(".") ? count.toFixed(1) : count}{suffix}</span>;
}

const HERO_BG = "https://images.unsplash.com/photo-1738996596694-93a6a2359b22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXIlMjBoaWdod2F5JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MzQzMjMzMHww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG = "https://images.unsplash.com/photo-1683650666910-ecc317a12061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxsYXRpbiUyMGZhbWlseSUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3NzM0NDQ4MDR8MA&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG_2 = "https://images.unsplash.com/photo-1694185766105-ab09b373e83a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxicmF6aWxpYW4lMjBmYW1pbHklMjBoYXBweSUyMGhvbWUlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzczNDQ0Nzk0fDA&ixlib=rb-4.1.0&q=85";

const partners = [
  { name: "Porto Seguro", logo: "/logos/porto-seguro.png" },
  { name: "Allianz", logo: "/logos/allianz.png" },
  { name: "Zurich", logo: "/logos/zurich.png" },
  { name: "SulAmérica", logo: "/logos/sulamerica.png" },
  { name: "Bradesco Seguros", logo: "/logos/bradesco.png" },
  { name: "Tokio Marine", logo: "/logos/tokio-marine.png" },
  { name: "HDI", logo: "/logos/hdi.png" },
  { name: "Itaú Seguros", logo: "/logos/itau.png" },
  { name: "Mapfre", logo: "/logos/mapfre.png" },
  { name: "Generali", logo: "/logos/generali.png" },
  { name: "Mitsui Sumitomo", logo: "/logos/mitsui.png" },
  { name: "Yelum", logo: "/logos/yelum.png" },
  { name: "MAG", logo: "/logos/mag.png" },
  { name: "Azul Seguros", logo: "/logos/azul.png" },
  { name: "Suhai", logo: "/logos/suhai.png" },
];

const products = [
  { icon: Car, title: "Seguro Auto", desc: "Proteção completa para seu veículo com as melhores coberturas do mercado.", href: "/seguro-auto", featured: true },
  { icon: Building2, title: "Seguro Empresarial", desc: "Proteja o patrimônio e as operações da sua empresa.", href: "/seguro-empresarial", featured: true },
  { icon: Heart, title: "Seguro de Vida", desc: "Segurança financeira para você e sua família.", href: "/seguro-de-vida", featured: true },
  { icon: HomeIcon, title: "Seguro Residencial", desc: "Proteção para sua casa, apartamento e seus bens.", href: "/cotacao" },
  { icon: Plane, title: "Seguro Viagem", desc: "Viaje tranquilo com cobertura nacional e internacional.", href: "/cotacao" },
  { icon: Smartphone, title: "Seguro Celular", desc: "Proteção contra roubo, furto e danos ao seu smartphone.", href: "/cotacao" },
  { icon: Bike, title: "Seguro Moto e Bike", desc: "Cobertura para motocicletas e bicicletas.", href: "/cotacao" },
  { icon: Truck, title: "Seguro Caminhão e Frota", desc: "Proteção para caminhões, frotas e transporte de cargas.", href: "/cotacao" },
  { icon: Tractor, title: "Seguro Agronegócio", desc: "Proteção para o produtor rural e suas operações.", href: "/cotacao" },
  { icon: Shield, title: "+20 Outros Seguros", desc: "Condomínio, fiança, equipamentos, eventos, RC e muito mais.", href: "/cotacao" },
];

const stats = [
  { num: "30", suffix: "+", label: "Anos de experiência" },
  { num: "15", suffix: "+", label: "Seguradoras parceiras" },
  { num: "5.0", suffix: "", label: "Avaliação Google" },
  { num: "3", suffix: "", label: "Escritórios", sublabel: "Belo Horizonte, Goiânia e Rio Quente" },
];

const reviews = [
  { name: "Sam Alme", text: "Há muitos anos sou cliente do Michel. Comecei com seu saudoso pai o Sr. Alcides. Sempre fui muito bem servido. Atendimento excepcional e de confiança!", rating: 5 },
  { name: "Bruno Pedrosa", text: "Precisei hoje de manhã, liguei para o Michel meu corretor que prontamente me atendeu e em 15 minutos o reboque já estava me socorrendo. Excelente!", rating: 5 },
  { name: "Dra. Helen Maia", text: "Eu e meus familiares somos clientes há mais de 15 anos. Excelente atendimento não somente na contratação do seguro quanto no atendimento diante de quaisquer intercorrências!", rating: 5 },
  { name: "Valeria Ribeiro", text: "Melhor corretora de Seguros de todas. Atendimento excelente! Melhor valor e boas condições de negociação! Eu e a minha família fazemos seguro de automóvel e residencial há anos com a Núcleo.", rating: 5 },
  { name: "Aline Montalvão", text: "Corretor Michel foi ágil e prestativo, no mesmo dia em que procurei o serviço fui prontamente atendida e meu carro segurado. Recomendo!", rating: 5 },
  { name: "Jacymara Paiva", text: "No WhatsApp, eu consegui agendar o atendimento. Serviço rápido e eficiente!", rating: 5 },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6 animate-fade-in">
                <Award className="h-4 w-4 text-gold" />
                <span className="text-sm text-white/90 font-medium">Corretora de seguros com tradição e confiança</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-5 animate-fade-in-up">
                Proteção sob medida <span className="text-gold">para você.</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl animate-fade-in-up anim-delay-2">
                Soluções completas em seguros para pessoas, famílias e empresas em todo o território nacional. Comparamos as melhores opções para você.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up anim-delay-3">
                <Link to="/cotacao">
                  <Button className="btn-gold h-12 px-8 text-base" data-testid="hero-cta-cotacao">
                    Solicitar Cotação <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href="https://wa.me/553125550207?text=Ol%C3%A1%2C%20gostaria%20de%20uma%20cota%C3%A7%C3%A3o." target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp('hero')}>
                  <Button className="btn-whatsapp h-12 px-8 text-base" data-testid="hero-cta-whatsapp">
                    Falar no WhatsApp
                  </Button>
                </a>
              </div>
            </div>
            {/* Hero Quote Widget */}
            <div className="hidden lg:block animate-fade-in-up anim-delay-3" data-testid="hero-quote-widget">
              <div className="bg-white/95 backdrop-blur-lg rounded-2xl p-6 shadow-2xl border border-white/50">
                <h3 className="font-heading font-bold text-navy text-lg mb-1">Cotação rápida e gratuita</h3>
                <p className="text-xs text-slate-500 mb-4">Resposta em minutos</p>
                <QuoteForm preselect="" compact />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Insurers Logos Bar */}
      <section className="bg-white border-b py-8" data-testid="insurers-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Seguradoras Parceiras</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-3 max-w-5xl mx-auto">
            {partners.map((ins, i) => (
              <div key={i} className="flex flex-col items-center justify-center h-[68px] bg-white rounded-xl border border-slate-100 hover:border-brand/30 hover:shadow-md transition-all p-2 gap-1" title={ins.name}>
                <img src={ins.logo} alt={ins.name} className="h-8 max-w-[100px] object-contain" loading="lazy" />
                <span className="text-[8px] font-semibold text-slate-400 leading-tight text-center">{ins.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <span className="text-3xl md:text-4xl font-heading font-bold text-brand">
                  <AnimatedCounter target={stat.num} suffix={stat.suffix} />
                </span>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                {stat.sublabel && <p className="text-xs text-slate-400 mt-0.5">{stat.sublabel}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compre Online - Featured Products */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-slate-50" data-testid="compre-online-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-gold/10 rounded-full px-3 py-1 mb-3">
                <Zap className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs font-semibold text-gold uppercase tracking-wider">100% Online</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-navy">Contrate agora, sem sair de casa</h2>
            </div>
            <Link to="/loja-online" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2.5 transition-all">
              Ver todos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "Seguro Celular", icon: Smartphone, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/9cfb28668fd7684e5280edea69b828164e0c70acc20fc64910bf99f18e3e5b72.png", url: "http://www.porto.vc/SEGUROCELULAR_B3773J_351a9551fbe34f27b78069b1c4fdd23f", tag: "Popular", price: "R$9,90/mês" },
              { name: "Seguro Auto", icon: Car, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/0f5738aa00cb79604800f7ceef4c0bc92547a6328a9a5e8bbf85713d6d7ea1a1.png", url: "http://www.porto.vc/SEGUROAUTO_B3773J_96945914e7ed434b88db180aa0ae9790", tag: "Mais Vendido", price: "R$89/mês" },
              { name: "Seguro Viagem", icon: Plane, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/39941944bb316681236a0791dcb91258f89921278a467f298552c95c1a9005f8.png", url: "http://www.porto.vc/VIAGEM_B3773J_0cdb8d9d99dd4da29839557b02992ea1", price: "R$4,50/dia" },
              { name: "Smart e Games", icon: Gamepad2, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/79404e16a2eb3ec6d2a211c82b71050b3a75719abdd6df67edf412752ed76402.png", url: "http://www.porto.vc/SEGUROSMARTEGAMES_B3773J_865af88b9a4b40c1b7e9bc9f83b5a02a", price: "R$12,90/mês" },
              { name: "Porto Serviços", icon: Wrench, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/72866f338866cc5fb5791e673c2fb899aa233f895715d0459524762f476ff8e2.png", url: "http://www.porto.vc/PORTOSERVICO_B3773J_558c2001d8ea4d30a786128e300ff92e", price: "R$99" },
              { name: "Cartão Porto Bank", icon: CreditCard, image: "https://static.prod-images.emergentagent.com/jobs/5132111f-2110-4c7f-a89a-a50af7bd2a7a/images/3ee81bcfef1b12029675e6803be5fcbc594cf9745e694cb8868d35e4f0cd212d.png", url: "http://www.porto.vc/CARTAODECREDITOPORTOBANK_B3773J_84509e6b2ef84504820812bdf5952762", tag: "Sem Anuidade", price: "Grátis" },
            ].map((item, i) => (
              <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`featured-product-${i}`}>
                <div className="relative h-28 overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  {item.tag && <span className="absolute top-2 left-2 bg-brand text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase">{item.tag}</span>}
                </div>
                <div className="p-3 text-center">
                  <h3 className="text-xs font-semibold text-navy group-hover:text-brand transition-colors">{item.name}</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">a partir de <span className="font-bold text-navy">{item.price}</span></p>
                  <span className="text-[10px] text-gold font-medium mt-1 flex items-center justify-center gap-0.5">Contratar <ArrowRight className="h-2.5 w-2.5" /></span>
                </div>
              </a>
            ))}
          </div>
          <div className="text-center mt-8 sm:hidden">
            <Link to="/loja-online">
              <Button className="btn-gold px-6" data-testid="ver-todos-mobile">Ver todos os produtos <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Por que escolher a Núcleo</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-4">Experiência que faz a diferença</h2>
            <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">Combinamos a agilidade digital com o atendimento consultivo de quem conhece o mercado há mais de três décadas.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: "30+ anos de experiência", desc: "Tradição e conhecimento profundo do mercado de seguros brasileiro." },
              { icon: Users, title: "Atendimento consultivo", desc: "Análise personalizada para encontrar a melhor cobertura para você." },
              { icon: Shield, title: "Principais seguradoras", desc: "Parceria com mais de 10 seguradoras para comparar e encontrar o melhor." },
              { icon: Star, title: "Custo-benefício", desc: "Excelente relação entre preço e cobertura para cada perfil." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-navy text-base mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 md:py-28" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Nossos Seguros</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-4">Proteção sob medida para cada necessidade</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {products.map((p, i) => (
              <Link key={i} to={p.href} className={`product-card group relative bg-white border rounded-xl p-5 hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden ${p.featured ? "border-brand/20" : "border-slate-200"}`} data-testid={`product-card-${i}`}>
                {p.featured && <div className="absolute top-2.5 right-2.5"><span className="text-[9px] font-semibold bg-gold/10 text-gold px-2 py-0.5 rounded-full uppercase">Destaque</span></div>}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${p.featured ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`}>
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-semibold text-sm text-navy mb-1.5 group-hover:text-brand transition-colors">{p.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-3">{p.desc}</p>
                <span className="text-xs font-medium text-brand flex items-center gap-1">
                  Saiba mais <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Snippet */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="about-snippet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Sobre a Núcleo</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-5">Mais de 30 anos protegendo famílias e empresas</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                Fundada em 1993 na região do Parque Lago das Rosas em Goiânia, a Núcleo Corretora de Seguros nasceu com o propósito de oferecer segurança, confiança e orientação técnica de qualidade. Com escritórios em Goiânia, Belo Horizonte e Rio Quente, atendemos clientes em todo o território nacional.
              </p>
              <ul className="space-y-3 mb-8">
                {["Comparação entre as principais seguradoras", "Atendimento personalizado e consultivo", "Suporte completo em sinistros", "Presença em Belo Horizonte, Goiânia e Rio Quente", "Cobertura em todo o território nacional e Mercosul"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-brand shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <Link to="/sobre">
                <Button variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white transition-all" data-testid="about-cta">
                  Conheça nossa história <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img src={FAMILY_IMG} alt="Família protegida" className="rounded-2xl shadow-xl w-full h-80 lg:h-96 object-cover" />
              <img src={FAMILY_IMG_2} alt="Proteção familiar" className="absolute -bottom-6 -right-6 w-36 h-36 rounded-xl shadow-lg object-cover border-4 border-white hidden lg:block" />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg p-4 border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Star className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-navy text-lg">5.0</p>
                    <p className="text-xs text-slate-500">Avaliação Google</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Avaliações Google</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-4">O que nossos clientes dizem</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl text-gold">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
              <span className="text-lg font-heading font-bold text-navy">5.0</span>
            </div>
            <p className="text-sm text-slate-500">Avaliação no Google</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow" data-testid={`review-card-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(r.rating)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 italic font-accent">"{r.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-brand">{r.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-navy">{r.name}</p>
                    <p className="text-xs text-slate-400">Avaliação Google</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Calculator */}
      <section className="py-20 md:py-28" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InsuranceCalculator />
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-navy py-16 md:py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Solicite sua cotação gratuita</h2>
              <p className="text-base text-slate-300 leading-relaxed mb-2">
                Preencha o formulário e receba uma proposta personalizada com as melhores opções de seguro para o seu perfil.
              </p>
              <p className="text-sm text-slate-400">Resposta em minutos.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
              <QuoteForm compact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
